import React from "react";
import {
  AlertIcon,
  ClockIcon,
  CodeIcon,
  CornerDownRightIcon,
  ErrorIcon,
  GlobeIcon,
  HeartIcon,
  LayoutIcon,
  LightningIcon,
  MegaphoneIcon,
  MinusCircleIcon,
  MoneyIcon,
  PersonIcon,
  PersonWithCheckIcon,
  RefreshIcon,
  SearchIcon,
  SettingsIcon,
  SparklesIcon,
  SuccessIcon,
  TargetIcon,
  TrendIcon,
  WarehouseIcon,
} from "ui/icons";
import { BookIcon } from "ui/icons/book";
import hero from "../../../../public/assets/cdp-industries/heros/technology-b2b-saas.png";
import { dataBreachStat, personalizedExperiencesStat, revenueReferenceCopy, type CdpIndustryConfig } from "./shared";

export const technologyB2bSaasCdpConfig: CdpIndustryConfig = {
  icon: WarehouseIcon,
  name: "technology & B2B SaaS",
  titleCaseName: "Technology & B2B SaaS",
  pluralNoun: "technology & B2B SaaS companies",
  color: "blue",
  solutionPageSlug: "/solutions/saas",
  seoCopy:
    "Leverage all your technology & B2B SaaS data, including accounts, users, subscriptions, transactions, product usage logs, and churn metrics.",
  heroImage: {
    src: hero,
    alt: "A photograph of four people sitting at a table with a list of example data tables overlayed. The data tables included are for Accounts, Product Usage Logs, Subscriptions, Users, and Churn Metrics.",
  },
  adoptingCdpsDescription: (
    <>
      AI has leveled the playing field in the world of technology, and great products are no longer enough.{" "}
      {revenueReferenceCopy}
    </>
  ),
  featuredOutcomes: [
    {
      icon: TrendIcon,
      color: "green",
      title: "Grow LTV & ARR",
    },
    {
      icon: RefreshIcon,
      color: "yellow",
      title: "Reduce churn",
    },
    {
      icon: HeartIcon,
      color: "red",
      title: "Build loyalty",
    },
  ],
  dataStatsTitleMaxW: "3xl",
  dataStats: [
    {
      title: "$11T",
      description: (
        <>
          Estimated global
          <br />
          tech spending by 2026
        </>
      ),
      href: "https://mgiresearch.com/research/the-global-tech-market-is-bigger-than-you-think/",
    },
    personalizedExperiencesStat,
    dataBreachStat,
  ],
  personalizationCards: [
    {
      title: "Hightouch creates a year-in-review campaign for all customers every year",
      href: "/blog/rewind-2024-in-review",
    },
    {
      title: "Docusign is powering customer 360 for the entire sales organization",
      href: "/customers/docusign",
    },
    {
      title:
        "Ramp has a fully automated outbound sales team which drives 25% of all sales pipeline via personalized emails",
      href: "/customers/ramp",
    },
  ],
  useCases: {
    performanceMarketing: [
      // First row
      {
        icon: PersonWithCheckIcon,
        title: "Acquisition campaigns",
        description:
          "Drive lead generation and new user acquisition by targeting decision-makers and lookalike audiences with firmographic and behavioral data, offering free trials or personalized demos.",
      },
      {
        icon: MinusCircleIcon,
        title: "Suppression campaigns",
        description:
          "Reduce wasted ad spend by suppressing active customers from acquisition campaigns using CRM data, reallocating budget toward net-new accounts to improve ROAS.",
      },
      {
        icon: TargetIcon,
        title: "Retargeting campaigns",
        description:
          "Re-engage users who visited the site or started a demo but didn’t convert by serving tailored ads emphasizing pain-point solutions, recovering lost leads and nurturing prospects.",
      },
      // Second row
      {
        icon: MoneyIcon,
        title: "Cross-sell campaigns",
        description:
          "Promote complementary SaaS tools or add-ons by using product usage data to target accounts with relevant upgrades, increasing account expansion and customer retention.",
      },
      {
        icon: MegaphoneIcon,
        title: "Referral campaigns",
        description:
          "Leverage existing users to generate new leads by offering referral incentives like free months or premium features, driving cost-effective signups through user advocacy.",
      },
      {
        icon: TrendIcon,
        title: "Account-based marketing campaigns",
        description:
          "Focus on specific high-value accounts or industries using a combination of personalized content, multi-channel outreach, and intent data to increase engagement and conversion rates among key decision-makers.",
      },
      // Third row
      {
        icon: ClockIcon,
        title: "Free trial promotions",
        description:
          "Acquire new users by advertising free trials to qualified prospects through personalized ads, increasing trial sign-ups and overall lead generation.",
      },
      {
        icon: SettingsIcon,
        title: "Conversion APIs",
        description:
          "Improve campaign optimization by sharing online and offline conversion data with ad platforms, enabling algorithms to better identify high-likelihood converters and optimize campaign performance.",
      },
      {
        icon: GlobeIcon,
        title: "Technology & B2B SaaS media networks",
        description:
          "Monetize software usage data by analyzing engagement patterns and creating high-value B2B audience segments, selling insights to SaaS providers targeting scaling businesses.",
      },
    ],
    lifecycleMarketing: [
      // First row
      {
        icon: MoneyIcon,
        title: "Upsell & cross-sell campaigns",
        description:
          "Promote advanced tiers, additional seats, or integrations based on product usage data, increasing ARR.",
      },
      {
        icon: PersonWithCheckIcon,
        title: "Onboarding campaigns",
        description:
          "Send step-by-step guides and in-app tips to help new users adopt the product quickly, improving activation.",
      },
      {
        icon: RefreshIcon,
        title: "Account reactivation campaigns",
        description:
          "Re-engage churned or inactive accounts by offering special discounts or trials, recovering lost revenue.",
      },
      // Second row
      {
        icon: TrendIcon,
        title: "Usage expansion campaigns",
        description:
          "Identify and promote underutilized features to existing users, increasing product adoption and engagement.",
      },
      {
        icon: ErrorIcon,
        title: "Churn prevention campaigns",
        description:
          "Reduce cancellations by proactively addressing customer pain points and providing personalized support.",
      },
      {
        icon: SparklesIcon,
        title: "Milestone celebrations",
        description:
          "Celebrate user achievements such as reaching reporting milestones, driving loyalty and retention.",
      },
    ],
    onsitePersonalization: [
      // First row
      {
        icon: LightningIcon,
        title: "Dynamic home page content",
        description:
          "Display personalized messaging based on firmographics to increase engagement and lead generation.",
      },
      {
        icon: PersonIcon,
        title: "Dynamic onboarding experiences",
        description:
          "Customize in-app onboarding flows based on user roles, highlighting relevant features like analytics for managers or collaboration tools for team members to streamline adoption.",
      },
      {
        icon: SearchIcon,
        title: "Dynamic search results",
        description:
          "Improve feature discovery by tailoring in-app search results to highlight popular tools, FAQs, or relevant integrations based on user behavior.",
      },
      // Second row
      {
        icon: CodeIcon,
        title: "Recommended features",
        description:
          "Suggest high-value features, such as automation tools or integrations, based on usage data to increase adoption and retention.",
      },
      {
        icon: CornerDownRightIcon,
        title: "Next best action",
        description:
          "Guide users toward impactful actions like adding team members, integrating with third-party tools, or setting up automations to drive deeper engagement.",
      },
      {
        icon: BookIcon,
        title: "Personalized resources",
        description:
          "Suggest relevant articles, guides, webinars, or case studies based on users' roles, industries, and past interactions with your platform to provide valuable insights and solutions tailored to specific users.",
      },
      // Third row
      {
        icon: LayoutIcon,
        title: "Dynamic features dashboard",
        description:
          "Personalize the user dashboard by highlighting relevant features based on user roles and past behavior to encourage feature adoption.",
      },
      {
        icon: AlertIcon,
        title: "Proactive trial reminders",
        description:
          "Send targeted reminders about expiring trials, emphasizing the benefits of upgrading to a paid subscription to improve conversion rates.",
      },
      {
        icon: SuccessIcon,
        title: "Usage goal tracking",
        description:
          "Reinforce engagement by showing users their progress toward automation, workflow completion, or other milestones, motivating them to fully adopt the product.",
      },
    ],
  },
  considerations: {
    dataModeling: {
      traditional: "Uses predefined models that may not fully capture user behaviors or SaaS account hierarchies",
      composable:
        "Supports tailored models to handle SaaS-specific data like account usage metrics, customer hierarchies, and churn risk",
    },
    audienceManagement: {
      traditional:
        "Supports broad segmentation but struggles with categories like product usage cohorts or account roles",
      composable: "Enables granular segmentation using engagement data, account types, and purchasing intent",
    },
    customerJourneys: {
      traditional: "Provides standard templates for campaigns like onboarding or feature announcements",
      composable:
        "Powers fully adaptable journeys for SaaS events like product adoption, upgrades, renewals, or account expansion",
    },
    identityResolution: {
      traditional:
        "Relies on out-of-the-box algorithms that may not unify profiles across CRM product logs and trial systems",
      composable:
        "Supports custom algorithms to unify customer profiles across CRMs, usage data, and customer support tools",
    },
  },
  whyHightouchTitleMaxW: "750px",
  audienceOverlap: {
    title: "Product evaluators who didn’t convert",
    firstCategoryLabel: "Free trial users who didn’t upgrade",
    secondCategoryLabel: "Webinar attendees",
  },
};
